import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useAllAppointments } from '@/hooks/useAllAppointments';
import { Calendar } from '@/components/ui/calendar';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { format } from 'date-fns';
import { UnifiedLoader } from '@/components/dashboard/shared/UnifiedLoader';
import { useTranslation } from 'react-i18next';
import { STANDARD_SESSION_FORMATS, normalizeSessionFormat } from '@/utils/session-format-helpers';
import { supabase } from '@/integrations/supabase/client';

const CalendarView: React.FC = () => {
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const { activeRole, user } = useAuth();
  const navigate = useNavigate();
  const { appointments: allAppointments, isLoading } = useAllAppointments();
  const { t } = useTranslation('dashboard');
  const [blackoutDates, setBlackoutDates] = useState<string[]>([]);

  // Filter out cancelled appointments as a safety measure
  const appointments = useMemo(() => {
    const filtered = allAppointments?.filter(apt => !apt.status?.includes('Cancelled')) || [];
    console.log('CalendarView - All appointments:', allAppointments?.length);
    console.log('CalendarView - Filtered appointments (no cancelled):', filtered.length);
    console.log('CalendarView - Filtered appointments:', filtered.map(a => ({ id: a.id, status: a.status })));
    return filtered;
  }, [allAppointments]);

  // Fetch practitioner's blackout dates and set up real-time subscription
  useEffect(() => {
    const fetchBlackoutDates = async () => {
      if (activeRole !== 'practitioner' || !user?.id) return;

      try {
        const { data, error } = await supabase
          .from('practitioners')
          .select('availability_rules')
          .eq('id', user.id)
          .single();

        if (!error && data?.availability_rules) {
          const rules = data.availability_rules as any;
          setBlackoutDates(rules.blackout_dates || []);
        }
      } catch (error) {
        console.error('Error fetching blackout dates:', error);
      }
    };

    fetchBlackoutDates();

    // Set up real-time subscription for availability updates
    if (activeRole === 'practitioner' && user?.id) {
      const channel = supabase
        .channel(`practitioner-availability-${user.id}`)
        .on(
          'postgres_changes',
          {
            event: 'UPDATE',
            schema: 'public',
            table: 'practitioners',
            filter: `id=eq.${user.id}`,
          },
          (payload) => {
            console.log('Practitioner availability updated:', payload);
            if (payload.new && payload.new.availability_rules) {
              const rules = payload.new.availability_rules as any;
              setBlackoutDates(rules.blackout_dates || []);
            }
          }
        )
        .subscribe();

      return () => {
        supabase.removeChannel(channel);
      };
    }
  }, [activeRole, user?.id]);

  // Get dates that have appointments
  const appointmentDates = useMemo(() => {
    if (!appointments?.length) return [];
    return appointments.map(apt => new Date(apt.start_time).toDateString());
  }, [appointments]);

  // Get blackout dates as Date strings for comparison
  const blackoutDateStrings = useMemo(() => {
    return blackoutDates.map(dateStr => {
      // Parse date in local timezone to avoid UTC conversion issues
      // If dateStr is "2025-11-16", create Date at local midnight
      const [year, month, day] = dateStr.split('-').map(Number);
      const localDate = new Date(year, month - 1, day);
      return localDate.toDateString();
    });
  }, [blackoutDates]);

  // Get appointments for selected date
  const selectedDateAppointments = useMemo(() => {
    if (!appointments?.length || !selectedDate) return [];
    const selectedDateString = selectedDate.toDateString();
    return appointments.filter(apt =>
      new Date(apt.start_time).toDateString() === selectedDateString
    );
  }, [appointments, selectedDate]);

  // Navigate to appointment details with proper tab and appointmentId
  const handleViewAppointment = useCallback((appointment: any) => {
    const appointmentTime = new Date(appointment.start_time);
    const now = new Date();
    const tab = appointmentTime < now ? 'history' : 'upcoming';
    
    // Navigate based on active role
    if (activeRole === 'client') {
      navigate(`/client-dashboard?tab=${tab}&appointmentId=${appointment.id}`);
    } else {
      navigate(`/practitioner-dashboard?tab=${tab}&appointmentId=${appointment.id}`);
    }
  }, [navigate, activeRole]);

  // Get status color for badges
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Completed': return 'bg-green-100 text-green-800';
      case 'Scheduled': return 'bg-blue-100 text-blue-800';
      case 'Cancelled': return 'bg-red-100 text-red-800';
      case 'Pending Payment': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (isLoading) {
    return <UnifiedLoader message="Loading calendar..." />;
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      <div>
        <h1 className="text-xl sm:text-2xl font-bold">{t('calendar.title')}</h1>
        <p className="text-gray-600 mt-2 text-sm sm:text-base">{t('calendar.description')}</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 sm:gap-6">
        {/* Calendar */}
        <div className="lg:col-span-2">
          <Card>
            <CardHeader className="pb-3 sm:pb-6">
              <CardTitle className="text-lg sm:text-xl">{t('calendar.calendarView')}</CardTitle>
            </CardHeader>
            <CardContent className="p-3 sm:p-6">
              <Calendar
                mode="single"
                selected={selectedDate}
                onSelect={(date) => date && setSelectedDate(date)}
                className="rounded-md border w-full pointer-events-auto scale-75 sm:scale-100"
                classNames={{
                  months: "flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0",
                  month: "space-y-4",
                  caption: "flex justify-center pt-1 relative items-center",
                  caption_label: "text-sm font-medium",
                  nav: "space-x-1 flex items-center",
                  nav_button: "h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100",
                  nav_button_previous: "absolute left-1",
                  nav_button_next: "absolute right-1",
                  table: "w-full border-collapse space-y-1",
                  head_row: "flex",
                  head_cell: "text-muted-foreground rounded-md w-8 sm:w-9 font-normal text-[0.8rem]",
                  row: "flex w-full mt-2",
                  cell: "relative p-0 text-center text-sm focus-within:relative focus-within:z-20 [&:has([aria-selected])]:bg-accent first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md",
                  day: "h-8 w-8 sm:h-9 sm:w-9 p-0 font-normal aria-selected:opacity-100 hover:bg-accent hover:text-accent-foreground rounded-md",
                  day_selected: "bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground focus:bg-primary focus:text-primary-foreground",
                  day_today: "bg-accent text-accent-foreground",
                  day_outside: "text-muted-foreground opacity-50",
                  day_disabled: "text-muted-foreground opacity-50",
                  day_range_middle: "aria-selected:bg-accent aria-selected:text-accent-foreground",
                  day_hidden: "invisible",
                }}
                modifiers={{
                  hasAppointment: (date) => appointmentDates.includes(date.toDateString()),
                  blockedDate: (date) => blackoutDateStrings.includes(date.toDateString())
                }}
                modifiersStyles={{
                  hasAppointment: {
                    backgroundColor: '#3b82f6',
                    color: 'white',
                    fontWeight: 'bold'
                  },
                  blockedDate: {
                    textDecoration: 'line-through',
                    backgroundColor: '#fee2e2',
                    color: '#991b1b',
                    opacity: 0.6
                  },
                  outside: {
                    visibility: 'hidden'
                  }
                }}
              />
            </CardContent>
          </Card>
        </div>

        {/* Selected Date Appointments */}
        <div>
          <Card>
            <CardHeader className="pb-3 sm:pb-6">
              <CardTitle className="text-base sm:text-lg break-words">
                {format(selectedDate, 'MMMM d, yyyy')}
              </CardTitle>
            </CardHeader>
            <CardContent className="p-3 sm:p-6">
              {selectedDateAppointments.length > 0 ? (
                <div className="space-y-2 sm:space-y-3">
                  {selectedDateAppointments.map((appointment) => {
                    const startTime = new Date(appointment.start_time);
                    const isHistorical = startTime < new Date();
                    
                    return (
                      <div
                        key={appointment.id}
                        className={`p-2 sm:p-3 rounded-lg border cursor-pointer transition-colors hover:bg-gray-50 ${
                          isHistorical ? 'border-gray-200 bg-gray-50/50' : 'border-blue-200 bg-blue-50/50'
                        }`}
                        onClick={() => handleViewAppointment(appointment)}
                      >
                        <div className="flex justify-between items-start mb-2">
                          <div className="text-xs sm:text-sm font-medium">
                            {format(startTime, 'h:mm a')} - {format(new Date(appointment.end_time), 'h:mm a')}
                          </div>
                          <Badge className={`text-xs ${getStatusColor(appointment.status)}`}>
                            {appointment.status}
                          </Badge>
                        </div>
                        
                        <div className="text-xs sm:text-sm text-gray-600">
                          {appointment.client_first_name} {appointment.client_last_name}
                        </div>
                        
                        <div className="text-xs text-gray-500 mt-1">
                          {normalizeSessionFormat(appointment.format) === STANDARD_SESSION_FORMATS.VIRTUAL ? '🌐 Online' : '📍 In-person'}
                        </div>
                        
                        {isHistorical && (
                          <div className="text-xs text-gray-400 mt-1">
                            Historical appointment
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
                ) : (
                <p className="text-gray-500 text-xs sm:text-sm text-center py-4">{t('calendar.noAppointments')}</p>
              )}
            </CardContent>
          </Card>

          {/* Statistics */}
          <Card className="mt-3 sm:mt-4">
            <CardHeader className="pb-3 sm:pb-6">
              <CardTitle className="text-base sm:text-lg">{t('calendar.overview')}</CardTitle>
            </CardHeader>
            <CardContent className="p-3 sm:p-6">
              <div className="space-y-2">
                <div className="flex justify-between text-xs sm:text-sm">
                  <span>{t('calendar.totalAppointments')}:</span>
                  <span className="font-medium">{appointments?.length || 0}</span>
                </div>
                <div className="flex justify-between text-xs sm:text-sm">
                  <span>{t('calendar.thisMonth')}:</span>
                  <span className="font-medium">
                    {appointments?.filter(apt => {
                      const appointmentDate = new Date(apt.start_time);
                      const now = new Date();
                      return appointmentDate.getMonth() === now.getMonth() && 
                             appointmentDate.getFullYear() === now.getFullYear();
                    }).length || 0}
                  </span>
                </div>
                <div className="flex justify-between text-xs sm:text-sm">
                  <span>{t('calendar.upcoming')}:</span>
                  <span className="font-medium">
                    {appointments?.filter(apt => new Date(apt.start_time) > new Date()).length || 0}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default CalendarView;